/**
 * DAS ADRESSBUCH (.358).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (14.10.2026)
 *   „Des Weiteren müssen wir ein globales Adressbuch einbinden. Was
 *    unterteilt ist, wo wir Gruppen anlegen können für beispielsweise
 *    Behörden, Telekom, Mitarbeiter für verschiedene Bereiche, Rufnummer,
 *    E-Mail-Adresse, Mobilnummer ... wo wir dann per Klick draufklicken
 *    können und dann direkt per Webex anrufen können."
 *   „Wir nutzen die WebEx App installiert auf dem PC. So klicken wir auch wie
 *    jetzt bisher einfach auf die Rufnummer. Es ist verlinkt und es wird
 *    darüber ausgewählt."
 *
 * Links die Gruppen (eigene mit Untergruppen, darunter die aus der
 * Plattform), rechts die Kontakte als Karten. Jede Nummer ist ein
 * `tel:`-Link: die Webex-App wählt. „Alle anschreiben" öffnet eine Mail an
 * alle Adressen der Gruppe.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, darf, type Anmelder } from '../api.ts';
import { Kopf } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { passtSuche } from '../teile/Schluesselsuche.tsx';
import { fragen, eingeben } from '../teile/rueckfrage.tsx';

type Wahl = { art: 'gruppe'; id: string } | { art: 'plattform'; schluessel: string } | { art: 'alle' } | { art: 'ausgeblendet' };
const LEER = { name: '', vorname: '', nachname: '', firma: '', funktion: '', telefon: '', mobil: '', email: '', notiz: '' };
/* .595: jede Nummer ist anklickbar (Webex wählt über tel:), auch wenn der Server keinen Link gebaut hat. */
const telAus = (n: string) => 'tel:' + String(n).replace(/[^\d+]/g, '');
const kuerzel = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]!.toUpperCase()).join('');

export default function Adressbuch({ ich, bereich = 'ne3' }: { ich: Anmelder; bereich?: 'ne3' | 'ne4' }) {
  /* .588: NE4 hat sein eigenes Buch (Marvin: „losgelöst von dem, was wir in NE3 haben ... Aufbau aber der gleiche"). */
  const [partner, setPartner] = useState<any | null>(null);
  const [d, setD] = useState<Awaited<ReturnType<typeof api.adressbuch>> | null>(null);
  const [wahl, setWahl] = useState<Wahl>({ art: 'alle' });
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [kontakt, setKontakt] = useState<any | null>(null);
  const [notizBei, setNotizBei] = useState<string | null>(null);
  const [notizText, setNotizText] = useState('');
  const notizSpeichern = async (k: any) => {
    const notiz = notizText.trim();
    const zeile = k.quelle_id ? (k.kontakt_id ?? null) : k.id;
    await tu(async () => {
      const w = { name: k.name, firma: k.firma, funktion: k.funktion, telefon: k.telefon, mobil: k.mobil, email: k.email, notiz,
                  vorname: k.vorname, nachname: k.nachname };
      if (zeile) await api.adressbuchKontaktAendern(zeile, { ...w, ...(k.gruppe_id ? { gruppe_id: k.gruppe_id } : {}) });
      else await api.adressbuchKontakt({ bereich, quelle: k.quelle_id ?? k.quelle ?? null, gruppe_id: k.gruppe_id ?? null, ...w });
      return notiz ? 'Notiz gespeichert.' : 'Notiz entfernt.';
    });
    setNotizBei(null);
  };
  const [gruppeNeu, setGruppeNeu] = useState<{ eltern_id: string | null; name: string } | null>(null);
  const darfBearbeiten = darf(ich, bereich === 'ne4' ? 'ne4.adressbuch.bearbeiten' : 'adressbuch.bearbeiten');

  const laden = async () => { try { setD(await api.adressbuch(bereich)); } catch (e) { setFehler(ausFehler(e)); } };
  useFrisch(laden);
  useEffect(() => { void laden(); }, []);

  const gruppen = d?.gruppen ?? [];
  const oben = gruppen.filter((g) => !g.eltern_id);
  const kinder = (id: string) => gruppen.filter((g) => g.eltern_id === id);
  const unterbaum = (id: string): string[] => [id, ...kinder(id).flatMap((k) => unterbaum(k.id))];
  const gruppeName = (id: string) => gruppen.find((g) => g.id === id)?.name ?? '';

  const sicht = useMemo(() => {
    if (!d) return [];
    let l: any[];
    /* Geänderte Plattform-Kontakte stehen in beiden Listen; in „Alle"
       zählt nur ihr Plattformeintrag (der trägt schon die geänderten Werte). */
    if (suche.trim() || wahl.art === 'alle') l = [...d.kontakte.filter((k) => !k.quelle), ...d.plattform.flatMap((p) => p.kontakte.map((k) => ({ ...k, herkunft: p.name })))];
    else if (wahl.art === 'ausgeblendet') l = d.ausgeblendet ?? [];
    else if (wahl.art === 'gruppe') { const ids = new Set(unterbaum(wahl.id)); l = d.kontakte.filter((k) => ids.has(k.gruppe_id)); }
    else l = (d.plattform.find((p) => p.schluessel === wahl.schluessel)?.kontakte ?? []).map((k) => ({ ...k, herkunft: d.plattform.find((p) => p.schluessel === wahl.schluessel)?.name }));
    return l.filter((k) => passtSuche(suche, k.name, k.firma, k.funktion, k.telefon, k.mobil, k.email, k.notiz,
        ...(k.ansprechpartner ?? []).flatMap((x: any) => [x.name, x.funktion, x.telefon, x.mobil, x.email])))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'de'));
  }, [d, wahl, suche]); // eslint-disable-line react-hooks/exhaustive-deps

  const titel = suche.trim() ? `Suche „${suche.trim()}"` : wahl.art === 'alle' ? 'Alle Kontakte' : wahl.art === 'ausgeblendet' ? 'Ausgeblendet'
    : wahl.art === 'gruppe' ? gruppeName(wahl.id) : d?.plattform.find((p) => p.schluessel === wahl.schluessel)?.name ?? '';
  const mails = [...new Set(sicht.map((k) => k.email).filter(Boolean))];

  async function tu(f: () => Promise<string | null>) {
    setFehler(null); setHinweis(null);
    try { const t = await f(); if (t) setHinweis(t); await laden(); } catch (e) { setFehler(ausFehler(e)); }
  }

  const gruppeKnopf = (g: any, tiefe: number) => (
    <div key={g.id}>
      <button type="button" className={`ab-gruppe ${wahl.art === 'gruppe' && wahl.id === g.id ? 'an' : ''}`}
              style={{ paddingLeft: 12 + tiefe * 16 }} aria-current={wahl.art === 'gruppe' && wahl.id === g.id}
              onClick={() => { setWahl({ art: 'gruppe', id: g.id }); setSuche(''); }}>
        <span>{g.name}</span><span className="ab-zahl">{unterbaum(g.id).reduce((s, id) => s + (gruppen.find((x) => x.id === id)?.anzahl ?? 0), 0)}</span>
      </button>
      {kinder(g.id).map((k) => gruppeKnopf(k, tiefe + 1))}
    </div>
  );

  return (
    <>
      <Kopf titel={bereich === 'ne4' ? 'NE4, Adressbuch' : 'Adressbuch'} weg="Gruppen, Einträge und Ansprechpartner, ein Klick auf die Nummer wählt über Webex"
            rechts={<input className="suche" type="search" autoComplete="off" value={suche} style={{ minWidth: 260 }}
                           placeholder="Suchen: Name, Firma, Nummer, Mail …" aria-label="Im Adressbuch suchen"
                           onChange={(e) => setSuche(e.target.value)} />} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}
        <div className="ab-rahmen">
          <nav className="ab-gruppen karte" aria-label="Gruppen">
            <button type="button" className={`ab-gruppe ${wahl.art === 'alle' && !suche ? 'an' : ''}`} onClick={() => { setWahl({ art: 'alle' }); setSuche(''); }}>
              <span>Alle</span>
            </button>
            <div className="ab-ueber">Eigene Gruppen</div>
            {oben.map((g) => gruppeKnopf(g, 0))}
            {darfBearbeiten && (
              <button type="button" className="knopf klein ab-neu" onClick={() => setGruppeNeu({ eltern_id: null, name: '' })}>+ Gruppe</button>
            )}
            <div className="ab-ueber">Aus der Plattform</div>
            {(d?.plattform ?? []).map((p) => (
              <button key={p.schluessel} type="button"
                      className={`ab-gruppe ${wahl.art === 'plattform' && wahl.schluessel === p.schluessel ? 'an' : ''}`}
                      onClick={() => { setWahl({ art: 'plattform', schluessel: p.schluessel }); setSuche(''); }}>
                <span>{p.name}</span><span className="ab-zahl">{p.kontakte.length}</span>
              </button>
            ))}
            {(d?.ausgeblendet ?? []).length > 0 && (
              <button type="button" className={`ab-gruppe ${wahl.art === 'ausgeblendet' ? 'an' : ''}`}
                      onClick={() => { setWahl({ art: 'ausgeblendet' }); setSuche(''); }}>
                <span className="leise">Ausgeblendet</span><span className="ab-zahl">{d!.ausgeblendet.length}</span>
              </button>
            )}
          </nav>

          <section className="ab-inhalt">
            <div className="ab-kopf">
              <h2 style={{ margin: 0 }}>{titel}</h2>
              <span className="klein leise">{sicht.length} {sicht.length === 1 ? 'Kontakt' : 'Kontakte'}</span>
              <span style={{ flex: 1 }} />
              {mails.length > 0 && (
                <a className="knopf klein" href={`mailto:?bcc=${encodeURIComponent(mails.join(','))}`}>Alle anschreiben ({mails.length})</a>
              )}
              {darfBearbeiten && wahl.art === 'gruppe' && !suche.trim() && (
                <>
                  <button type="button" className="knopf klein" onClick={() => setGruppeNeu({ eltern_id: wahl.id, name: '' })}>+ Untergruppe</button>
                  <button type="button" className="knopf klein" onClick={async () => {
                    const n = await eingeben('Neuer Name der Gruppe:', gruppeName(wahl.id));
                    if (n && n.trim()) void tu(async () => { await api.adressbuchGruppeAendern(wahl.id, n.trim()); return null; });
                  }}>Umbenennen</button>
                  <button type="button" className="knopf klein" onClick={async () => {
                    if (!await fragen(`Gruppe „${gruppeName(wahl.id)}" entfernen?`)) return;
                    void tu(async () => {
                      try { await api.adressbuchGruppeLoeschen(wahl.id); }
                      catch (e: any) {
                        if (e?.daten?.feld === 'mit_kontakten' && await fragen(`${e.message} Jetzt mit allen Kontakten entfernen?`)) await api.adressbuchGruppeLoeschen(wahl.id, true);
                        else throw e;
                      }
                      setWahl({ art: 'alle' }); return 'Gruppe entfernt.';
                    });
                  }}>Entfernen</button>
                  <button type="button" className="knopf klein haupt" onClick={() => setKontakt({ ...LEER, gruppe_id: wahl.id })}>+ Kontakt</button>
                </>
              )}
              {darfBearbeiten && wahl.art !== 'gruppe' && !suche.trim() && oben.length > 0 && (
                <button type="button" className="knopf klein haupt" onClick={() => setKontakt({ ...LEER, gruppe_id: oben[0].id })}>+ Kontakt</button>
              )}
            </div>
            {wahl.art === 'plattform' && !suche.trim() && (
              <p className="klein leise" style={{ marginTop: 0 }}>
                Diese Kontakte kommen aus der Plattform ({({ mitarbeiter: 'Benutzer und Rechte', firmen: 'Stammdaten, Firmen', gemeinden: 'Behördenmappe, Gemeinden', ne4firmen: 'Benutzer und Rechte, Firmen und Teams', verwaltungen: 'NE4, Verwaltungen der Häuser' } as Record<string, string>)[wahl.schluessel] ?? 'Plattform'}).
                „Ändern" und „Entfernen" wirken nur im Adressbuch, die Stammdaten bleiben, wie sie sind.
              </p>
            )}
            {!d ? <p className="klein leise">Wird geladen.</p>
              : !sicht.length ? <p className="klein leise">{suche.trim() ? 'Nichts gefunden.' : 'Noch keine Kontakte in dieser Gruppe.'}</p>
              : (
                <div className="ab-karten">
                  {sicht.map((k) => (
                    <article key={k.id} className="ab-karte">
                      <div className="ab-person">
                        <span className="ab-kreis" aria-hidden="true">{kuerzel(k.name ?? '?')}</span>
                        <div style={{ minWidth: 0 }}>
                          <b>{k.name}</b>
                          <div className="klein leise">{[k.funktion, k.firma !== k.name ? k.firma : null, k.gruppe_id ? gruppeName(k.gruppe_id) : null, k.herkunft].filter(Boolean).join(', ')}</div>
                        </div>
                      </div>
                      <div className="ab-wege">
                        {k.mobil && (k.mobil_link
                          ? <a className="ab-ruf haupt" href={k.mobil_link} title="Über Webex anrufen">Mobil {k.mobil}</a>
                          : <a className="ab-ruf haupt" href={telAus(k.mobil)} title="Anrufen">Mobil {k.mobil}</a>)}
                        {k.telefon && (k.telefon_link
                          ? <a className={`ab-ruf ${k.mobil ? '' : 'haupt'}`} href={k.telefon_link} title="Über Webex anrufen">Telefon {k.telefon}</a>
                          : <a className={`ab-ruf ${k.mobil ? '' : 'haupt'}`} href={telAus(k.telefon)} title="Anrufen">Telefon {k.telefon}</a>)}
                        {k.email && <a className="ab-mail" href={`mailto:${k.email}`}>{k.email}</a>}
                        {!k.mobil && !k.telefon && !k.email && <span className="klein leise">keine Nummer, keine Mail</span>}
                      </div>
                      {/* .615: DIE NOTIZ DIREKT AM KONTAKT, ohne das Fenster „Ändern". Marvin, 03.11.2026: „den Kontakten in den
                          Adressbüchern, NE3, NE4, muss ein Feld sein für Notizen, dass man da noch was ergänzen kann als
                          Gedankenstütze." Klick auf die Notiz (oder „Notiz ergänzen") öffnet das Feld an Ort und Stelle;
                          Speichern mit dem Knopf oder Strg+Enter, Escape verwirft. */}
                      {notizBei === k.id ? (
                        <div style={{ marginTop: 4 }}>
                          <textarea className="ab-notizfeld" value={notizText} rows={3} autoFocus placeholder="Gedankenstütze zu diesem Kontakt"
                                    onChange={(e) => setNotizText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Escape') setNotizBei(null); if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void notizSpeichern(k); }} />
                          <div className="reihe" style={{ gap: 6, marginTop: 4 }}>
                            <button type="button" className="knopf klein haupt" onClick={() => void notizSpeichern(k)}>Notiz speichern</button>
                            <button type="button" className="knopf klein" onClick={() => setNotizBei(null)}>Abbrechen</button>
                          </div>
                        </div>
                      ) : k.notiz ? (
                        <div className={'klein ab-notiz' + (darfBearbeiten ? ' klickbar' : '')} style={{ whiteSpace: 'pre-wrap' }} title={darfBearbeiten ? 'Notiz ändern' : undefined}
                             onClick={() => { if (darfBearbeiten) { setNotizBei(k.id); setNotizText(k.notiz ?? ''); } }}>📝 {k.notiz}</div>
                      ) : darfBearbeiten && wahl.art !== 'ausgeblendet' ? (
                        <button type="button" className="alslink klein leise" onClick={() => { setNotizBei(k.id); setNotizText(''); }}>Notiz ergänzen</button>
                      ) : null}
                      {/* .588: ANSPRECHPARTNER UNTER DEM EINTRAG. Marvin, 02.11.2026: „Ich habe eine Firma, beziehungsweise
                          einen Eintrag und habe dort aber verschiedene Ansprechpartner drunter. Das fehlt auch bei NE3." */}
                      {(k.ansprechpartner ?? []).length > 0 && (
                        <div className="ab-partner">
                          {(k.ansprechpartner ?? []).map((x: any) => (
                            <div key={x.id} className="ab-partner-zeile">
                              <div><b>{x.name}</b>{x.funktion && <span className="klein leise">, {x.funktion}</span>}</div>
                              <div className="ab-wege">
                                {x.mobil && <a className="ab-ruf" href={x.mobil_link || telAus(x.mobil)} title="Über Webex anrufen">Mobil {x.mobil}</a>}
                                {x.telefon && <a className="ab-ruf" href={x.telefon_link || telAus(x.telefon)} title="Über Webex anrufen">Telefon {x.telefon}</a>}
                                {x.email && <a className="ab-mail" href={`mailto:${x.email}`}>{x.email}</a>}
                              </div>
                              {x.notiz && <div className="klein leise" style={{ whiteSpace: 'pre-wrap' }}>{x.notiz}</div>}
                              {darfBearbeiten && (
                                <div className="reihe" style={{ gap: 8 }}>
                                  <button type="button" className="alslink klein" onClick={() => setPartner({ ...LEER, ...x, vorname: x.vorname ?? '', nachname: x.nachname ?? (x.vorname ? '' : x.name), zeile: x.id, eintrag: k.name })}>Ändern</button>
                                  <button type="button" className="alslink klein" onClick={async () => {
                                    if (!await fragen(`${x.name} als Ansprechpartner bei ${k.name} entfernen?`)) return;
                                    void tu(async () => { await api.adressbuchPartnerLoeschen(x.id); return `${x.name} entfernt.`; });
                                  }}>Entfernen</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* ÄNDERN UND ENTFERNEN AN JEDEM KONTAKT (.359). Marvin,
                          14.10.2026: „ich kann im adressbuch niemand editieren
                          ... niemanden löschen ... keiner gruppe zuweisen".
                          Bis .358 hatten nur eigene Kontakte die beiden Knöpfe. */}
                      {darfBearbeiten && wahl.art === 'ausgeblendet' && (
                        <div className="reihe" style={{ gap: 8 }}>
                          <button type="button" className="alslink klein" onClick={() => void tu(async () => {
                            await api.adressbuchKontaktLoeschen(k.kontakt_id, true); return `${k.name} steht wieder im Adressbuch.`;
                          })}>Wieder einblenden</button>
                        </div>
                      )}
                      {darfBearbeiten && wahl.art !== 'ausgeblendet' && (
                        <div className="reihe" style={{ gap: 8 }}>
                          <button type="button" className="alslink klein" onClick={() => setKontakt({
                            ...LEER, ...k,
                            /* zeile: was geändert wird; quelle: der Plattformeintrag, falls es noch keine Zeile gibt */
                            zeile: k.quelle_id ? (k.kontakt_id ?? null) : k.id,
                            quelle: k.quelle_id ?? k.quelle ?? null,
                            gruppe_id: k.gruppe_id ?? '',
                            vorname: k.vorname ?? '', nachname: k.nachname ?? (k.vorname ? '' : k.name),
                          })}>Ändern</button>
                          <button type="button" className="alslink klein" onClick={() => setPartner({
                            ...LEER, eintrag: k.name,
                            ...(k.quelle_id ? { quelle: k.quelle_id } : { kontakt_id: k.id }),
                          })}>+ Ansprechpartner</button>
                          <button type="button" className="alslink klein" onClick={async () => {
                            const platt = !!(k.quelle_id || k.quelle);
                            if (!await fragen(platt
                              ? `${k.name} im Adressbuch ausblenden? Die Stammdaten bleiben, unter „Ausgeblendet" lässt sich der Kontakt zurückholen.`
                              : `${k.name} aus dem Adressbuch entfernen?`)) return;
                            void tu(async () => {
                              const zeile = k.quelle_id ? k.kontakt_id : k.id;
                              if (zeile) await api.adressbuchKontaktLoeschen(zeile);
                              else await api.adressbuchKontakt({ bereich, quelle: k.quelle_id, ausgeblendet: true, name: k.name, firma: k.firma, funktion: k.funktion,
                                                                 telefon: k.telefon, mobil: k.mobil, email: k.email, notiz: k.notiz });
                              return platt ? `${k.name} ausgeblendet.` : `${k.name} entfernt.`;
                            });
                          }}>Entfernen</button>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
          </section>
        </div>
      </div>

      {gruppeNeu && (
        <Dialog titel={gruppeNeu.eltern_id ? `Untergruppe in „${gruppeName(gruppeNeu.eltern_id)}"` : 'Neue Gruppe'} breite={420} zu={() => setGruppeNeu(null)}>
          <Text name="name" titel="Name" pflicht wert={gruppeNeu.name} setze={(w) => setGruppeNeu({ ...gruppeNeu, name: w })} breite="100%" />
          <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="knopf" onClick={() => setGruppeNeu(null)}>Abbrechen</button>
            <button type="button" className="knopf haupt" disabled={!gruppeNeu.name.trim()} onClick={() => {
              const g = gruppeNeu; setGruppeNeu(null);
              void tu(async () => { const r = await api.adressbuchGruppe({ name: g.name.trim(), eltern_id: g.eltern_id, bereich }); setWahl({ art: 'gruppe', id: r.id }); return null; });
            }}>Anlegen</button>
          </div>
        </Dialog>
      )}

      {kontakt && (
        <Dialog titel={kontakt.zeile || kontakt.quelle ? `${kontakt.name} ändern` : 'Neuer Kontakt'} breite={560} zu={() => setKontakt(null)}>
          <div className="dialogleib">
            <div className="feld" style={{ width: '100%' }}>
              <label htmlFor="ab-gruppe">Gruppe</label>
              <select id="ab-gruppe" value={kontakt.gruppe_id ?? ''} onChange={(e) => setKontakt({ ...kontakt, gruppe_id: e.target.value })}>
                {kontakt.quelle && <option value="">(keine Gruppe, nur aus der Plattform)</option>}
                {gruppen.map((g) => <option key={g.id} value={g.id}>{g.eltern_id ? `${gruppeName(g.eltern_id)} › ` : ''}{g.name}</option>)}
              </select>
            </div>
            {/* .595: Vorname und Nachname getrennt (Marvin). Eine Stelle ohne Person steht unter Nachname oder Firma. */}
            <div className="reihe" style={{ gap: 8 }}>
              <Text name="vorname" titel="Vorname" wert={kontakt.vorname ?? ''} setze={(w) => setKontakt({ ...kontakt, vorname: w })} breite="100%" fehler={fehler} />
              <Text name="nachname" titel="Nachname" pflicht wert={kontakt.nachname ?? ''} setze={(w) => setKontakt({ ...kontakt, nachname: w })} breite="100%" fehler={fehler} />
            </div>
            <Text name="firma" titel="Firma oder Stelle" wert={kontakt.firma} setze={(w) => setKontakt({ ...kontakt, firma: w })} breite="100%" />
            <Text name="funktion" titel="Funktion oder Bereich" wert={kontakt.funktion} setze={(w) => setKontakt({ ...kontakt, funktion: w })} breite="100%" />
            <Text name="mobil" titel="Mobil" art="tel" wert={kontakt.mobil} setze={(w) => setKontakt({ ...kontakt, mobil: w })} breite="100%" />
            <Text name="telefon" titel="Telefon" art="tel" wert={kontakt.telefon} setze={(w) => setKontakt({ ...kontakt, telefon: w })} breite="100%" />
            <Text name="email" titel="E-Mail" art="email" wert={kontakt.email} setze={(w) => setKontakt({ ...kontakt, email: w })} breite="100%" fehler={fehler} />
            <Text name="notiz" titel="Notiz" mehrzeilig wert={kontakt.notiz} setze={(w) => setKontakt({ ...kontakt, notiz: w })} breite="100%" />
          </div>
          <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="knopf" onClick={() => setKontakt(null)}>Abbrechen</button>
            <button type="button" className="knopf haupt" disabled={!String(kontakt.nachname ?? '').trim() && !String(kontakt.vorname ?? '').trim()} onClick={async () => {
              setFehler(null);
              try {
                const w = { gruppe_id: kontakt.gruppe_id || null, vorname: kontakt.vorname, nachname: kontakt.nachname, name: [kontakt.vorname, kontakt.nachname].map((x: any) => String(x ?? '').trim()).filter(Boolean).join(' '), firma: kontakt.firma, funktion: kontakt.funktion,
                            telefon: kontakt.telefon, mobil: kontakt.mobil, email: kontakt.email, notiz: kontakt.notiz };
                if (kontakt.zeile) await api.adressbuchKontaktAendern(kontakt.zeile, w);
                else await api.adressbuchKontakt({ ...w, quelle: kontakt.quelle ?? null, bereich });
                setHinweis(`${[kontakt.vorname, kontakt.nachname].filter(Boolean).join(' ') || kontakt.name} gespeichert.`); setKontakt(null); await laden();
              } catch (e) { setFehler(ausFehler(e)); }
            }}>Speichern</button>
          </div>
        </Dialog>
      )}
      {partner && (
        <Dialog titel={partner.zeile ? `${partner.name} ändern` : `Ansprechpartner bei ${partner.eintrag}`} breite={520} zu={() => setPartner(null)}>
          <div className="dialogleib">
            <div className="reihe" style={{ gap: 8 }}>
              <Text name="vorname" titel="Vorname" wert={partner.vorname ?? ''} setze={(w) => setPartner({ ...partner, vorname: w })} breite="100%" fehler={fehler} />
              <Text name="nachname" titel="Nachname" pflicht wert={partner.nachname ?? ''} setze={(w) => setPartner({ ...partner, nachname: w })} breite="100%" fehler={fehler} />
            </div>
            <Text name="funktion" titel="Funktion oder Bereich" wert={partner.funktion} setze={(w) => setPartner({ ...partner, funktion: w })} breite="100%" platzhalter="z. B. Bauleitung, Disposition, Hausmeister" />
            <Text name="mobil" titel="Mobil" art="tel" wert={partner.mobil} setze={(w) => setPartner({ ...partner, mobil: w })} breite="100%" />
            <Text name="telefon" titel="Telefon" art="tel" wert={partner.telefon} setze={(w) => setPartner({ ...partner, telefon: w })} breite="100%" />
            <Text name="email" titel="E-Mail" art="email" wert={partner.email} setze={(w) => setPartner({ ...partner, email: w })} breite="100%" fehler={fehler} />
            <Text name="notiz" titel="Notiz" mehrzeilig wert={partner.notiz} setze={(w) => setPartner({ ...partner, notiz: w })} breite="100%" />
          </div>
          <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="knopf" onClick={() => setPartner(null)}>Abbrechen</button>
            <button type="button" className="knopf haupt" disabled={!String(partner.nachname ?? '').trim() && !String(partner.vorname ?? '').trim()} onClick={async () => {
              setFehler(null);
              try {
                const w = { vorname: partner.vorname, nachname: partner.nachname, name: [partner.vorname, partner.nachname].map((x: any) => String(x ?? '').trim()).filter(Boolean).join(' '), funktion: partner.funktion, telefon: partner.telefon, mobil: partner.mobil, email: partner.email, notiz: partner.notiz };
                if (partner.zeile) await api.adressbuchPartnerAendern(partner.zeile, w);
                else await api.adressbuchPartner({ ...w, bereich, kontakt_id: partner.kontakt_id ?? null, quelle: partner.quelle ?? null });
                setHinweis(`${[partner.vorname, partner.nachname].filter(Boolean).join(' ') || partner.name} gespeichert.`); setPartner(null); await laden();
              } catch (e) { setFehler(ausFehler(e)); }
            }}>Speichern</button>
          </div>
        </Dialog>
      )}
    </>
  );
}
